import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from "firebase/messaging";

class FirebaseNotificationService {
  constructor() {
    this.messaging = null;
    this.isInitialized = false;
    this.vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || process.env.REACT_APP_FIREBASE_VAPID_KEY;
    this.firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY || process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || process.env.REACT_APP_FIREBASE_PROJECT_ID || "yes-charge",
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID || process.env.REACT_APP_FIREBASE_APP_ID
    };
    

    this.pendingUserId = null;
    this.initializationPromise = null;
  }

  async initialize(userId = null) {
    
    if (this.isInitialized) {
      if (userId) {
        await this.setupAndRegisterToken(userId);
      } else if (this.pendingUserId) {
        await this.setupAndRegisterToken(this.pendingUserId);
        this.pendingUserId = null;
      }
      return true;
    }


    if (userId) {
      this.pendingUserId = userId;
    }

    try {
      const app = initializeApp(this.firebaseConfig);
      
      const supported = await isSupported();
      
      if (supported) {
        this.messaging = getMessaging(app);
        this.isInitialized = true;
      
        this.setupForegroundHandler();
        
        this.setupServiceWorkerListener();

        this.registerWithServiceWorker();
        
        if (this.pendingUserId) {
          await this.setupAndRegisterToken(this.pendingUserId);
          this.pendingUserId = null;
        }
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Agent Portal - Firebase initialization error:', error);
      return false;
    }
  }

  async setupAndRegisterToken(userId) {
  
  try {
    if (!this.isInitialized) {
      const initialized = await this.initialize(userId);
      if (!initialized) {
        console.error('❌ Agent Portal - Firebase initialization failed');
        return false;
      }
      return true;
    }


    let permission = Notification.permission;
    
    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch (permissionError) {
        console.error('Agent Portal - Error requesting permission:', permissionError);
        permission = 'denied';
      }
    }
    
    
    if (permission !== 'granted') {
      return false;
    }

    const token = await this.getFCMToken();
    
    if (!token) {
      console.error('Agent Portal - Failed to get FCM token');
      return false;
    }


    return token; 
    
  } catch (error) {
    console.error('Agent Portal - setupAndRegisterToken() ERROR:', error);
    return false;
  }
}
  async getFCMToken() {
    
    try {
      if (!this.messaging) {
        return null;
      }

      const permission = Notification.permission;
      if (permission !== 'granted') {
        return null;
      }


      const currentToken = await getToken(this.messaging, { 
        vapidKey: this.vapidKey 
      });
      
      if (currentToken) {
        return currentToken;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Agent Portal - Error getting FCM token:', error);
      return null;
    }
  }


  async requestPermission() {
    
    try {
      
      if (!this.isSecureContext()) {
        return 'denied';
      }
      
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Agent Portal - Error requesting notification permission:', error);
      return 'denied';
    }
  }

  setupForegroundHandler() {
    
    if (!this.messaging) {
      console.error('Agent Portal - Messaging not available for foreground handler');
      return;
    }
    
    onMessage(this.messaging, (payload) => {
      
      this.showInAppNotification(payload);

      this.dispatchNotificationEvent(payload);
      
      return payload;
    });

  }

  setupServiceWorkerListener() {
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        
        if (event.data.type === 'PUSH_NOTIFICATION') {

     
          this.showInAppNotification(event.data.payload);
          this.dispatchNotificationEvent(event.data.payload);
          
          if (document.visibilityState === 'visible') {
            this.showBrowserNotification(event.data.payload);
          }
        }
      });

    } else {
      console.warn('Agent Portal - Service workers not supported in this browser');
    }
  }

  registerWithServiceWorker() {
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'REGISTER_CLIENT',
        timestamp: new Date().toISOString(),
        portal: 'agent'
      });
    } else {
      console.warn('Agent Portal - Service worker controller not available');
    }
  }

  showInAppNotification(payload) {
    
    if (!payload.notification && !payload.data) {
      return;
    }
    
    const notification = {
      id: payload.data?.notificationId || `push_${Date.now()}`,
      title: payload.notification?.title || payload.data?.title || 'Notification',
      description: payload.notification?.body || payload.data?.body || '',
      type: payload.data?.type || 'info',
      notiType: payload.data?.notiType || 'push_notification',
      from: payload.data?.from || 'System',
      createdAt: new Date().toISOString(),
      isRead: false,
      data: payload.data || {},
      source: 'firebase_push',
      portal: 'agent'
    };
    
    this.storeNotificationInLocalStorage(notification);
    
    this.updateBadgeCount();
    
    return notification;
  }

  storeNotificationInLocalStorage(notification) {
    try {
      const existing = JSON.parse(localStorage.getItem('agent_notifications') || '[]');
      
      const isDuplicate = existing.some(n => n.id === notification.id);
      if (isDuplicate) {
        return;
      }
      
      const updated = [notification, ...existing].slice(0, 100);
      localStorage.setItem('agent_notifications', JSON.stringify(updated));
      
    } catch (error) {
      console.error('Agent Portal - Error storing notification in localStorage:', error);
    }
  }

  dispatchNotificationEvent(payload) {

    const event = new CustomEvent('agent-firebase-message', { 
      detail: payload 
    });
    window.dispatchEvent(event);

    const notificationEvent = new CustomEvent('agent-new-push-notification', {
      detail: {
        id: payload.data?.notificationId || `push_${Date.now()}`,
        title: payload.notification?.title || 'Notification',
        body: payload.notification?.body || '',
        data: payload.data,
        timestamp: new Date().toISOString(),
        portal: 'agent'
      }
    });
    window.dispatchEvent(notificationEvent);
  }

  updateBadgeCount() {
    try {
      const notifications = JSON.parse(localStorage.getItem('agent_notifications') || '[]');
      const unreadCount = notifications.filter(n => !n.isRead).length;
      
      document.title = unreadCount > 0 
        ? `(${unreadCount}) Agent Portal` 
        : 'Agent Portal';

      const event = new CustomEvent('agent-notification-badge-update', {
        detail: { count: unreadCount }
      });
      window.dispatchEvent(event);
      
      return unreadCount;
    } catch (error) {
      console.error('❌ Agent Portal - Error updating badge count:', error);
      return 0;
    }
  }

  showBrowserNotification(payload) {
    if (!payload.notification) return;
    
    const title = payload.notification.title || 'Notification';
    const options = {
      body: payload.notification.body || '',
      icon: '/logo.png',
      badge: '/logo.png',
      data: payload.data,
      tag: payload.data?.notificationId || `notification_${Date.now()}`
    };
    
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, options);
      
      notification.onclick = () => {
        console.log('Agent Portal - Browser notification clicked:', payload.data);
        window.focus();
        notification.close();
        
        if (payload.data?.orderId) {
          window.location.href = `/orders/${payload.data.orderId}`;
        }
      };
    }
  }

  async debugSetup() {
    
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log('10. Service Worker Registrations:', registrations.length);
      registrations.forEach((reg, i) => {
        console.log(`   ${i + 1}. Scope: ${reg.scope}, State: ${reg.active?.state}`);
      });
    }
    
  }

  async forceTokenRegistration(userId) {
    return await this.setupAndRegisterToken(userId);
  }

  async deleteToken() {
    try {
      if (!this.messaging) return;
      
      const token = await getToken(this.messaging);
      if (token) {
        await deleteToken(this.messaging);
      }
    } catch (error) {
      console.error('Agent Portal - Error deleting FCM token:', error);
    }
  }

  onMessageListener() {
    return new Promise((resolve) => {
      if (!this.messaging) {
        console.warn('Agent Portal - Messaging not initialized');
        resolve(null);
        return;
      }
      
      onMessage(this.messaging, (payload) => {
        resolve(payload);
        
        this.showInAppNotification(payload);
        this.dispatchNotificationEvent(payload);
      });
    });
  }

  getStoredNotifications() {
    try {
      return JSON.parse(localStorage.getItem('agent_notifications') || '[]');
    } catch (error) {
      console.error('Agent Portal - Error getting stored notifications:', error);
      return [];
    }
  }

  markNotificationAsRead(notificationId) {
    try {
      const notifications = this.getStoredNotifications();
      const updated = notifications.map(notif => 
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      );
      localStorage.setItem('agent_notifications', JSON.stringify(updated));
      this.updateBadgeCount();
      return true;
    } catch (error) {
      console.error('Agent Portal - Error marking notification as read:', error);
      return false;
    }
  }

  clearAllNotifications() {
    try {
      localStorage.removeItem('agent_notifications');
      this.updateBadgeCount();
      return true;
    } catch (error) {
      console.error('Agent Portal - Error clearing notifications:', error);
      return false;
    }
  }

  isPermissionGranted() {
    return Notification.permission === 'granted';
  }

  isPermissionDenied() {
    return Notification.permission === 'denied';
  }

  isPermissionDefault() {
    return Notification.permission === 'default';
  }

  isSecureContext() {
    return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  }
}

export default new FirebaseNotificationService();